import React from "react";
import { QueryRenderer } from "react-relay";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { graphql } from "relay-runtime";

import { getIntegrationForKey } from "~/integrations/actionItemIntegration";
import { ItemSelect, mapSelectorItemsToReactSelect } from "~/integrations/actionItemIntegration/selectors";
import { environment } from "~/models";
import Tippy from "~/ui/Tooltip";

import type { MondayColumnsSelectorsQuery as ColumnsQueryType } from "~/__generated__/MondayColumnsSelectorsQuery.graphql";

import mondayModal from "../css/mondayModal.module.css";

const columnsQuery = graphql`
	query MondayColumnsSelectorsQuery($boardId: String!) {
		viewer {
			integrations {
				monday {
					allColumns(boardId: $boardId) {
						id
						name
						columnType
						externalId
					}
				}
			}
		}
	}
`;

type mondayColumnResponseType = NonNullable<ColumnsQueryType["response"]["viewer"]["integrations"]["monday"]>;
type allColumnsType = NonNullable<mondayColumnResponseType["allColumns"]>;
type singleColumnType = NonNullable<allColumnsType[0]>;

export function ColumnsSelectors({
	boardId,
	assigneeColumnId,
	dueDateColumnId,
	statusColumnId,
	setAssigneeColumnId,
	setDueDateColumnId,
	setStatusColumnId,
	showLabels,
}: Partial<ColumnsQueryType["variables"]> & {
	assigneeColumnId?: string;
	dueDateColumnId?: string;
	statusColumnId?: string;
	setAssigneeColumnId: (assigneeColumnId: string) => void;
	setDueDateColumnId: (dueDateColumnId: string) => void;
	setStatusColumnId: (statusColumnId: string) => void;
	showLabels: boolean;
}) {
	const disabled = boardId === undefined;
	const { displayName } = getIntegrationForKey("MONDAY");

	const renderColumnsSelectors = React.useCallback(
		({ props }: { props: ColumnsQueryType["response"] | null }) => {
			let assigneeColumns: singleColumnType[] = [];
			let dueDateColumns: singleColumnType[] = [];
			let statusColumns: singleColumnType[] = [];

			const allColumns: allColumnsType | null | undefined = props?.viewer?.integrations?.monday?.allColumns;

			// Sort the Monday columns by type
			if (allColumns) {
				for (let column of allColumns) {
					switch (column.columnType) {
						case "people":
							assigneeColumns.push(column);
							break;
						case "date":
							dueDateColumns.push(column);
							break;
						case "status":
							statusColumns.push(column);
							break;
						default:
							break;
					}
				}
			}

			const assigneeColumnOptions = mapSelectorItemsToReactSelect(assigneeColumns);
			const dueDateColumnOptions = mapSelectorItemsToReactSelect(dueDateColumns);
			const statusColumnOptions = mapSelectorItemsToReactSelect(statusColumns);

			const ColumnLabelTippy = ({ name }: { name: string }) =>
				showLabels ? (
					<div className={mondayModal.label}>
						{name}
						<Tippy placement="right" content={`Sync this column from ${displayName} to this item`}>
							<div>
								<FontAwesomeIcon icon={faInfoCircle} />
							</div>
						</Tippy>
					</div>
				) : null;

			return (
				<>
					<label className={mondayModal.assignee}>
						<ColumnLabelTippy name="Assignee" />
						<ItemSelect
							shouldAssumeDefault
							selectFirstItem
							placeholder="Assignee"
							options={assigneeColumnOptions}
							selectedId={assigneeColumnId}
							setSelectedId={setAssigneeColumnId}
							isLoading={!props}
							isDisabled={disabled}
						/>
					</label>
					<label className={mondayModal.dueDate}>
						<ColumnLabelTippy name="Due date" />
						<ItemSelect
							shouldAssumeDefault
							selectFirstItem
							placeholder="Due date"
							options={dueDateColumnOptions}
							selectedId={dueDateColumnId}
							setSelectedId={setDueDateColumnId}
							isLoading={!props}
							isDisabled={disabled}
						/>
					</label>
					<label className={mondayModal.status}>
						<ColumnLabelTippy name="Status" />
						<ItemSelect
							shouldAssumeDefault
							selectFirstItem
							placeholder="Status"
							options={statusColumnOptions}
							selectedId={statusColumnId}
							setSelectedId={setStatusColumnId}
							isLoading={!props}
							isDisabled={disabled}
						/>
					</label>
				</>
			);
		},
		[
			disabled,
			assigneeColumnId,
			setAssigneeColumnId,
			dueDateColumnId,
			setDueDateColumnId,
			statusColumnId,
			setStatusColumnId,
			showLabels,
			displayName,
		],
	);

	return (
		<QueryRenderer<ColumnsQueryType>
			environment={environment}
			query={boardId ? columnsQuery : null}
			variables={{ boardId: boardId ?? "" }}
			render={renderColumnsSelectors}
		/>
	);
}
